/**
 * Internal Purchase Order Printing Utility
 * Generates formatted HTML for printing internal purchase orders
 */

import html2canvas from "html2canvas";

/**
 * Format date for display
 * @param {string} date - Date string
 * @returns {string} Formatted date
 */
export const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const purposeLabels = {
  tools_and_equipment: "Tools & Equipment",
  restock: "Restock",
  customer_po_fulfillment: "Item Order for Government PO",
  others: "Others",
};

/**
 * Generate the printable HTML document for an internal purchase order
 * @param {Object} po - Internal purchase order object
 * @returns {string} Full HTML document
 */
export const generatePrintHTML = (po) => {
  const items = Array.isArray(po.items) ? po.items : [];

  const itemsContent = items.length
    ? items
        .map(
          (item, index) => `
        <tr>
          <td style="text-align: center;">${index + 1}</td>
          <td>${item.item_name || "-"}</td>
          <td style="text-align: center;">${item.qty || "-"}</td>
          <td style="text-align: center;">${item.unit || "-"}</td>
          <td>${item.remarks || ""}</td>
        </tr>
      `
        )
        .join("")
    : `
        <tr>
          <td style="text-align: center;">1</td>
          <td>${po.description || "-"}</td>
          <td style="text-align: center;">-</td>
          <td style="text-align: center;">-</td>
          <td></td>
        </tr>
      `;

  const purpose = purposeLabels[po.purpose] || po.purpose || "-";

  return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Purchase Request - PR-${String(po.id).padStart(4, "0")}</title>
          <style>
            @media print {
              @page {
                margin: 0.5in;
                size: letter;
              }
              body { margin: 0; }
            }
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              margin: 0;
              padding: 20px;
              color: #2c3e50;
              font-size: 12px;
              line-height: 1.4;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 15px;
              border-bottom: 3px double #2563eb;
            }
            .company-name {
              font-size: 24px;
              font-weight: bold;
              color: #2563eb;
              letter-spacing: 2px;
              margin-bottom: 3px;
            }
            .company-sub {
              font-size: 11px;
              color: #555;
            }
            .document-title {
              font-size: 16px;
              color: #555;
              font-weight: 600;
              margin-top: 8px;
            }
            .info-section {
              display: table;
              width: 100%;
              margin-bottom: 15px;
            }
            .info-row {
              display: table-row;
            }
            .info-label {
              display: table-cell;
              font-weight: 600;
              padding: 3px 10px 3px 0;
              width: 140px;
              color: #444;
            }
            .info-value {
              display: table-cell;
              padding: 3px 0;
            }
            table.items {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            table.items th {
              background: #f1f5f9;
              border: 1px solid #cbd5e1;
              padding: 6px 8px;
              text-align: left;
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            table.items td {
              border: 1px solid #cbd5e1;
              padding: 6px 8px;
            }
            .remarks-box {
              margin-top: 15px;
              padding: 10px;
              border: 1px solid #cbd5e1;
              border-radius: 4px;
              background: #f8fafc;
            }
            .remarks-box .label {
              font-weight: 600;
              font-size: 11px;
              text-transform: uppercase;
              color: #444;
              margin-bottom: 4px;
            }
            .signatures {
              display: table;
              width: 100%;
              margin-top: 60px;
            }
            .signature-cell {
              display: table-cell;
              width: 33%;
              text-align: center;
              padding: 0 15px;
            }
            .signature-line {
              border-top: 1px solid #2c3e50;
              padding-top: 5px;
              font-size: 11px;
              font-weight: 600;
            }
            .signature-role {
              font-size: 10px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">JMB COMPUTERS</div>
            <div class="company-sub">
              Computer &amp; Laptop Repair Services &mdash; Cantilan, Surigao del Sur
            </div>
            <div class="document-title">PURCHASE REQUEST</div>
          </div>

          <div class="info-section">
            <div class="info-row">
              <div class="info-label">PR No.:</div>
              <div class="info-value"><strong>PR-${String(po.id).padStart(
                4,
                "0"
              )}</strong></div>
            </div>
            <div class="info-row">
              <div class="info-label">PO Date:</div>
              <div class="info-value">${formatDate(po.po_date)}</div>
            </div>
            <div class="info-row">
              <div class="info-label">Purpose:</div>
              <div class="info-value">${purpose}</div>
            </div>
            <div class="info-row">
              <div class="info-label">Status:</div>
              <div class="info-value">${po.status || "-"}</div>
            </div>
            <div class="info-row">
              <div class="info-label">Prepared By:</div>
              <div class="info-value">${
                po.created_by && po.created_by.name ? po.created_by.name : "-"
              }</div>
            </div>
          </div>

          <table class="items">
            <thead>
              <tr>
                <th style="width: 40px; text-align: center;">#</th>
                <th>Item Type / Description</th>
                <th style="width: 70px; text-align: center;">Qty</th>
                <th style="width: 90px; text-align: center;">Unit</th>
                <th style="width: 160px;">Remarks</th>
              </tr>
            </thead>
            <tbody>
              ${itemsContent}
            </tbody>
          </table>

          ${
            po.description
              ? `
          <div class="remarks-box">
            <div class="label">Description</div>
            <div>${po.description}</div>
          </div>
          `
              : ""
          }

          <div class="signatures">
            <div class="signature-cell">
              <div class="signature-line">Prepared By</div>
              <div class="signature-role">Signature over printed name</div>
            </div>
            <div class="signature-cell">
              <div class="signature-line">Approved By</div>
              <div class="signature-role">Signature over printed name</div>
            </div>
            <div class="signature-cell">
              <div class="signature-line">Received By</div>
              <div class="signature-role">Signature over printed name</div>
            </div>
          </div>
        </body>
        </html>
      `;
};

/**
 * Open the internal purchase order in a print window (user can print or save as PDF)
 * @param {Object} po - Internal purchase order object
 */
export const printInternalPurchaseOrder = (po) => {
  const printWindow = window.open("", "", "width=800,height=600");
  printWindow.document.write(generatePrintHTML(po));
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
};

/**
 * Copy the internal purchase order as an image to the clipboard
 * @param {Object} po - Internal purchase order object
 * @returns {Promise<boolean>} Whether copy succeeded
 */
export const copyInternalPurchaseOrderAsImage = async (po) => {
  // Create an offscreen container to render the HTML
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.left = "-9999px";
  container.style.top = "0";
  container.style.width = "800px";
  container.style.background = "white";
  container.style.zIndex = "-1";

  // Get the HTML body content (strip the full document wrapper)
  const fullHTML = generatePrintHTML(po);
  const bodyMatch = fullHTML.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  const styleMatch = fullHTML.match(/<style[^>]*>([\s\S]*?)<\/style>/i);

  if (styleMatch) {
    const styleEl = document.createElement("style");
    styleEl.textContent = styleMatch[1];
    container.appendChild(styleEl);
  }

  const content = document.createElement("div");
  content.innerHTML = bodyMatch ? bodyMatch[1] : fullHTML;
  content.style.padding = "20px";
  content.style.fontFamily = "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif";
  content.style.color = "#2c3e50";
  content.style.fontSize = "12px";
  content.style.lineHeight = "1.4";
  container.appendChild(content);

  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      width: 800,
      windowWidth: 800,
    });

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/png")
    );

    // eslint-disable-next-line no-undef
    await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);

    return true;
  } finally {
    document.body.removeChild(container);
  }
};
